import React, { useMemo } from 'react'

import { Section, SectionHeading, Card, Button } from '../../../components/ui'
import { bookingPath, enterBooking } from '../../../lib/booking-entry'
import { image } from '../../../lib/image'
import { washesFrom } from '../../../lib/service-catalogue'
import { numberWord } from '../../../lib/number-word'
import { usePublishedCatalogue } from '../../../lib/use-published-catalogue'
import { whatsappChatProps } from '../../../components/whatsapp-chat'
import { HERO_FRAMES, FRAME_W, FRAME_H, frameCaption } from '../../home/sections/hero/frames'
import { readWashes, readPrices } from '../../../lib/page-prices'
import { largeCarLine } from '../../../lib/price-lines'
import { DESCRIPTORS, PAIRS, FEATURE_GROUPS, FEATURE_COUNT, scores, includedBy, includedCount } from './washes'

import './compare.css'

// THE COMPARISON. The reason /services exists.
//
// The home page's whats-included section answers "which of the three do I
// want". This answers the other question: "show me everything and let me
// compare". Same catalogue, same row wording, six washes instead of three.
//
// WHAT THIS REPLACES, and why none of it survived.
//
// The old block was a six column matrix in which every column was a flat
// colour fill: #F88247 orange, #FDC455 yellow, #2ABFF3 light blue, #0070D7
// blue, #0A2540 navy and #6A6A6A grey, with the unfilled cells striped
// #f8f8f8. DESIGN.md §3 bans all seven of those by name. It also carried a
// "Popular" ribbon on Mini Valet PLUS, which §3 bans by name as well, and the
// prices were 14px white text inside the coloured headers, which is the one
// number on the page and it was the hardest thing on it to read.
//
// The fills were not a style choice that aged badly. They were doing the
// comparison: the eye followed the colour, not the ticks, and the darkest
// column read as "the best one" when it was simply the most expensive. On a
// site whose home page now has ten tokens and no badges, it contradicted
// itself one click from the fold.
//
// SO WHAT SEPARATES THE COLUMNS NOW. Rules, weight and space, all of which
// survive being read in greyscale:
//
//   1. A 1px --ink rule under the header row and a 1px --line rule under
//      every body row. --line is 1px everywhere by the palette (§1), so the
//      header rule is told apart by being darker, not thicker.
//   2. The price is Instrument Serif at --fs-total in --ink on --paper, which
//      is 28px of black on white at 1440. It was 14px of white on orange.
//   3. The ticks are --accent, which is exactly what the palette lists accent
//      for, and they are the only colour in the table.
//   4. Two groups, "Outside the car" and "Inside the car", so a column's
//      empty half is visible as a block of white rather than as fourteen
//      scattered gaps.
//   5. A 1px --line between the six wash columns. Fourteen rows of centred
//      ticks with nothing between them let the eye drift one column sideways
//      halfway down; the rule is what holds a reading of a single column
//      straight. It stops at each group band, so the two halves of the table
//      still read as two blocks.
//
// THE TIGHTENING PASS, 4 October 2026. The shape above survived the owner's
// read of it. The execution did not: "still too big, not properly legible,
// we need an easier way to compare". Five things came out, none of them type
// size, because the floor is --fs-caption and nothing a customer reads goes
// under it (§1).
//
//   1. The descriptors became a ladder. Six free-written sentences of three
//      and four lines each became six lines of the same shape: what happens
//      outside, then what happens inside. The argument is in ./washes.js.
//      That is 27px off the header row and, far more to the point, it turns
//      comparing two washes into comparing a handful of named jobs. Not one
//      row label was shortened to get there: the rows are what a valeter
//      would list and they are the reason anybody believes the page.
//   2. The label column went 248px to 312px and the wash columns 152px to
//      140px. Nine of the fourteen row labels were wrapping to two and three
//      lines at 248px; ten of them now fit on one. 294px of row height for
//      nothing but a column boundary moved.
//   3. Row padding went --space-12 to --space-8 top and bottom. A table row
//      is not a tap target: the only things in this table anybody taps are
//      the six buttons in the foot, which keep --control-h.
//   4. The table went 1160px wide to 1152px, which is exactly the container
//      at 1440 (1200px less two 24px gutters). It was eight pixels too wide
//      and scrolled sideways at the one width this page is designed at.
//   5. A column highlight on hover, in CSS, no state. With the row highlight
//      that was already here it draws a crosshair through the cell under the
//      pointer, which is the answer to "does the Full Valet include this
//      one" without the eye tracking across 312px of label and five other
//      columns to get there.
//
// WHAT WAS CONSIDERED AND REJECTED.
//
//   A sticky header row. The table is 1052px tall now and 980px of that is
//   the header and the fourteen rows, so at 1440 x 900 it is within a screen
//   and a bit and the header leaves the viewport only near the end of the
//   scroll. A sticky thead costs 186px of a 900px screen permanently, under
//   a 72px site header that is already sticky, to buy back that last part.
//   Not a trade worth making at this height. It would have been at 1393px.
//
//   More, smaller groups. Three or four bands scan better than fourteen flat
//   rows, which is why there are two. A third band costs 44px and there is
//   no third axis to cut on: outside and inside is the axis the catalogue is
//   actually built on, it is what the descriptors now say, and it is what
//   makes a column's empty half mean something.
//
//   Shorter row labels. All fourteen would fit one line at a narrower label
//   column if the words were cut, and that is the one saving on this page
//   that would have cost something real. "Door shuts, door cards, centre
//   console and dash wiped" is what somebody who does the job would write
//   down, and "Interior wiped" is what somebody selling software would. The
//   rows are the page's evidence. They are also printed verbatim by
//   pages/home/sections/whats-included, which is outside this slot. Widening
//   the column got ten of them onto one line and changed no word of any.
//
// NO COLUMN IS SINGLED OUT. All six booking buttons are --ink outlines and
// none is filled: a filled button under one of six columns is the "Popular"
// ribbon with the label taken off, and this page's job is to compare, not to
// steer. The page's one ink-filled primary action (§3) is the final CTA at
// the foot, which belongs to no single wash.
//
// THE TWO SAME-PRICE PAIRS. Exterior Wash & Wax and Mini Valet are both £69
// and they are not nested: Mini Valet has no wax, so "everything in the
// previous one, plus" is not true of them and no column ordering can make it
// true. Full Valet and Dog Owners Valet are both £215 and differ the same
// way. "Words and price" (§1) has no price to work with in either pair, so
// the words carry it: a one line descriptor under every price, and the two
// sentences above the table that name the difference outright. Neither
// sentence types a number: the amount comes from the meta tag and the clause
// only appears when the two prices really are equal.
//
// WHAT IT BECOMES AT 390px, and why. Six columns cannot be a table on a
// phone. Scrolling the table sideways was tried on the old page in the form
// of a react-slick carousel and it is the worse of the two options even done
// properly: at 140px a column, six columns is 840px of travel, and the row
// labels run off the left edge, so by column four the ticks have nothing to
// mean. So below 960px the same data is six stacked cards, each carrying its
// whole list under the same two group headings in the same row order.
//
// AND THE LIST IS FOLDED. Six open lists is fifty two items, and it measured
// 4,130px: nobody compares anything across four thousand pixels of scroll,
// they pick whichever card they happen to stop on. Each card's list is now a
// <details>, shut, with the count on the summary. Shut, the six cards are a
// menu: name, price, the two line ladder, how many of the fourteen rows it
// scores, and the button. 3, 4, 8, 5, 12 and 10 down one screen is a
// comparison a phone can actually hold, and the list is one tap under it.
//
// <details> and not state, for the same reason the two renderings are both in
// the markup: it is one element, it needs no JavaScript, it cannot shift the
// layout on first paint, and a crawler and a screen reader both read what is
// inside a shut one. The summary is --control-h tall, so it clears 44 x 44
// (§3) at every width.
//
// Both renderings are in the markup and CSS shows one, which is why there is
// no `Hidden`, no useMediaQuery and no layout shift on first paint. The cost
// is about 3 KB of duplicated list text; the thing it buys is that the
// hidden half is `display: none`, so no screen reader and no crawler reads
// the page twice.
//
// The table is also the one element on this page allowed to scroll
// sideways (§3). It is 1152px wide, which is the 1200px container less its
// two 24px gutters, so at 1440 it fits exactly and does not scroll at all; it
// scrolls inside its own box between 960 and 1200. The body never scrolls
// sideways at any width. It was 1160px before this pass, which is eight
// pixels wider than the container it sits in, so the one width this page is
// designed at was the width it quietly overflowed.

// THERE IS NO PRICE SKELETON ON THIS SECTION ANY MORE, and that is the whole
// change. A `Price` component that rendered an empty box when it had no number
// was the right answer while the page decided for itself which six washes
// exist: the name was certain and the number was not. The page no longer
// decides, so a wash and its price arrive together or neither arrives, and an
// empty box has nothing left to mean. The skeleton moved up a level, to the
// list (WashesPending below), where it is honest about what is missing.

// Two borders on an 11 x 6 box, rotated. The same tick as the home page's
// three cards, drawn in CSS rather than imported: an MUI icon for one glyph
// is not a trade worth making. The old table used <CheckRounded> at
// fontSize="large", which is the whole @material-ui/icons surface for a tick.
const Tick = () => <span className="svc-tick" aria-hidden="true" />

// href AND handler on every bookable row (§3): the href is what a crawler, a
// middle click and a long press follow, the handler is what keeps the SPA
// from reloading the bundle.
//
// `svc-book` is on both renderings and carries no styles. It is the hook the
// e2e suite uses to find a bookable tile at either width, replacing the four
// `.services-plans-*` selectors it used to reach into the old matrix and the
// old carousel with. One class, both layouts, and it does not move when the
// table's or the card's own styling does.
const BookButton = ({ wash, source, className }) => (
  <Button
    className={`svc-book ${className}`}
    variant="secondary"
    block
    href={bookingPath(wash.slug)}
    onClick={event => {
      event.preventDefault()
      enterBooking(wash.slug, source)
    }}>
    Book<span className="gwmc-sr-only"> {wash.name}</span>
  </Button>
)

// The wash's own page, beside its Book button: a label that says where it
// goes (Robin, 25 Oct 2026). Nothing when the wash has no page.
const InfoLink = ({ wash, className }) =>
  wash.path ? (
    <a className={`gwmc-link svc-info ${className}`} href={wash.path}>
      See what&apos;s included<span className="gwmc-sr-only"> in the {wash.name}</span>
    </a>
  ) : null

// One of the two same-price sentences. The price clause is printed only when
// the catalogue really does give the pair the same amount; otherwise the
// sentence still runs, without claiming a number it has not been told.
//
// A pair whose two washes are not both on the page prints NOTHING. The pairs
// used to be two indexes into a fixed array of six, which could only ever be
// right while that array was written in the bundle. Now that the list is the
// database's, a sentence comparing a column nobody can see would be the same
// class of bug as the page it replaces: copy that outlived its service.
const PairNote = ({ pair, washes }) => {
  const first = washes.find(wash => wash.slug === pair.washes[0])
  const second = washes.find(wash => wash.slug === pair.washes[1])

  if (!first || !second) return null

  const same = first.price && first.price === second.price

  return (
    <p className="svc-pair">
      <strong className="svc-pair-lead">
        {first.name} and {second.name} are {same ? `both ${first.price}` : 'not the same job'}.
      </strong>{' '}
      {pair.note}
    </p>
  )
}


// THE THREE PHOTOGRAPHS, and the argument for putting them here.
//
// This page had none. The rebuild deleted the stock collage that used to sit
// beside the H1 and put nothing back, on the grounds that the ten real frames
// are already the home page's filmstrip and recent-work strip and a third
// copy is decoration. The owner disagreed, and he is right: this is a pricing
// page, and a visitor here is reading fourteen rows of text about what gets
// wiped and vacuumed with nothing on screen to remind them what they are
// buying. The table cannot say "a van, a real street, a gloved hand". These
// can.
//
// WHERE, so it costs nothing. The lede is a heading, one line of intro and
// two short sentences, all of it inside --container-read, which leaves about
// 460px of empty paper to its right at 1440. Three portrait frames fit that
// column in less height than the words beside them take, so at 1440 this
// block adds nothing to the page at all. At 390 it falls under the two
// sentences and above the cards, which is roughly 750px down: below the
// phone fold (§5), so the H1 is still the LCP element and the thing the old
// collage broke stays fixed.
//
// WHICH THREE, and why these are not the home page's strip over again:
//
//   01  the operator in hi-vis with the van doors open and the kit out. The
//       only frame we own that shows the work, the van and a person at once,
//       and the only one that says "mobile" without a caption.
//   10  a gloved hand buffing a panel with a microfibre. The detail the word
//       "wax" in the table stands for.
//   09  an engine bay before and after. That is the row that separates the
//       two £215 washes, which is the hardest thing on this page to explain
//       in words, and the pair sentence beside it is explaining exactly that.
//
// So it is three frames chosen against the rows of this table, not nine
// frames in a scrolling strip. No peek, no snap, no scroll: a fixed trio, a
// different object from the hero's filmstrip and from recent-work's rail.
//
// The captions are the point, and they are the real service and the real
// outward postcode for that exact photograph: "Full Valet, SW19" is a claim
// stock photography cannot make. Under the frame, never on it, and no
// gradient over any of them (§3).
//
// The data is imported and not copied. Order::LiveFeed::SHOWCASE_TILES is the
// source of truth for the service, the postcode and the alt text, frames.js
// is its one mirror in the bundle and spec/design/derived_values_spec.rb
// asserts the two agree. recent-work already imports it across slots for the
// same reason: alt text that lives in four places drifts in three of them.
// frames.js imports no stylesheet, so nothing of the hero's comes with it.
const PROOF_FILES = ['gallery-image-01.webp', 'gallery-image-10.webp', 'gallery-image-09.webp']

const PROOF_FRAMES = PROOF_FILES.map(file => HERO_FRAMES.find(frame => frame.file === file)).filter(Boolean)

const Proof = () => (
  <div className="svc-proof">
    {PROOF_FRAMES.map(frame => (
      <figure className="svc-proof-frame" key={frame.file}>
        <img
          className="svc-proof-img"
          src={image(`./${frame.file}`)}
          alt={frame.alt}
          width={FRAME_W}
          height={FRAME_H}
          loading="lazy"
          decoding="async"
        />
        <figcaption className="svc-proof-caption">{frameCaption(frame)}</figcaption>
      </figure>
    ))}
  </div>
)

// "Large car +£20" under every price, table and cards (19 Oct 2026,
// docs/DECISIONS.md), from gwmc:prices like the rest of the page. No amount,
// no line.
const Large = () => {
  const line = largeCarLine(readPrices().large_car)
  return line ? <span className="svc-large">{line}</span> : null
}

const ComparisonTable = ({ washes }) => (
  <div className="svc-scroll">
    <table className="svc-table">
      <caption className="gwmc-sr-only">
        What each of the {numberWord(washes.length)} washes includes, and what each one costs.
      </caption>

      <colgroup>
        <col className="svc-col-label" />
        {washes.map(wash => (
          <col key={wash.slug} />
        ))}
      </colgroup>

      <thead>
        <tr>
          <td className="svc-corner" />
          {washes.map(wash => (
            <th scope="col" className="svc-head" key={wash.slug}>
              <span className="svc-head-name"><WashName wash={wash} /></span>
              {/* No price skeleton here any more. A wash with no price is not
                  in `washes` at all (lib/service-catalogue.js drops it), so
                  this cannot render an empty box: either the column is here
                  with its number or there is no column. */}
              <span className="svc-price">{wash.price}</span>
              <Large />
              <span className="svc-head-desc">{wash.descriptor}</span>
            </th>
          ))}
        </tr>
      </thead>

      {FEATURE_GROUPS.map(group => (
        <tbody key={group.id}>
          <tr className="svc-group">
            <th scope="colgroup" colSpan={washes.length + 1} className="svc-group-label">
              {/* The band spans the whole table, so the label inside it is
                  what sticks to the left edge while the columns scroll. */}
              <span className="svc-group-text">{group.title}</span>
            </th>
          </tr>

          {group.rows.map(row => (
            <tr className="svc-row" key={row.name}>
              <th scope="row" className="svc-row-label">
                {row.name}
              </th>
              {washes.map(wash => (
                <td className="svc-cell" key={wash.slug}>
                  {scores(row, wash.slug) ? <Tick /> : null}
                  <span className="gwmc-sr-only">
                    {scores(row, wash.slug) ? 'Included in' : 'Not included in'} {wash.name}
                  </span>
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      ))}

      <tfoot>
        <tr>
          <td className="svc-corner" />
          {washes.map(wash => (
            <td className="svc-foot" key={wash.slug}>
              <BookButton wash={wash} source="services_table_column" className="svc-foot-btn" />
              <InfoLink wash={wash} className="svc-foot-info" />
            </td>
          ))}
        </tr>
      </tfoot>
    </table>
  </div>
)

// One wash, whole, for a phone. The list is the same rows in the same order
// as its column in the table, under the same two headings, so somebody who
// read the page on a laptop and came back on a phone is reading the same
// thing in a different shape.
//
// The name and the price share a line. On a phone that is the pair somebody
// is reading anyway, and stacking them cost 32px on every card for nothing.
// The wash's own page (/services/<slug>), when it has one: `path` comes off
// gwmc:catalogue with the name. A plain link, because the page is
// server-rendered and reached with a document load (pages/services/wash).
const WashName = ({ wash }) =>
  wash.path ? (
    <a className="gwmc-link" href={wash.path}>
      {wash.name}
    </a>
  ) : (
    wash.name
  )

const WashCard = ({ wash }) => {
  const count = includedCount(wash.slug)

  return (
    <Card as="article" className="svc-card">
      <div className="svc-card-top">
        <h3 className="svc-card-name"><WashName wash={wash} /></h3>
        <span className="svc-price">{wash.price}</span>
      </div>
      <Large />

      <p className="svc-card-desc">{wash.descriptor}</p>

      {/* Shut. The count is what makes the cards comparable, so it is on the
          summary and not inside the fold. */}
      <details className="svc-card-detail">
        <summary className="svc-card-summary">
          <span className="svc-card-summary-text">What&apos;s included</span>
          <span className="svc-card-count">
            {count} of {FEATURE_COUNT}
          </span>
        </summary>

        {includedBy(wash.slug).map(group => (
          <div className="svc-card-group" key={group.id}>
            <h4 className="svc-card-group-title">{group.title}</h4>

            {group.items.length === 0 ? (
              <p className="svc-card-none">{group.none}</p>
            ) : (
              <ul className="svc-card-list">
                {group.items.map(item => (
                  <li className="svc-card-item" key={item}>
                    <Tick />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </details>

      <BookButton wash={wash} source="services_card" className="svc-card-btn" />
      <InfoLink wash={wash} className="svc-card-info" />
    </Card>
  )
}

// WHAT THE PAGE SHOWS WHILE IT HAS NO LIST, AND WHAT IT SHOWS IF IT NEVER
// GETS ONE.
//
// Almost nobody sees either. The list is in the HTML (gwmc:catalogue), so it is
// there before the bundle runs; the only ways to arrive here are an HTML
// document cached from before that tag existed, or a visitor whose browser
// got the document but cannot reach /api/v2/services.
//
// Loading is three blank bars at roughly the height the table's first rows
// occupy, never an empty section: a heading with nothing under it reads as "we
// sell nothing", and the bars hold some of the height so the arrival moves
// less of the page.
//
// Failing says so, in words, and hands over the way to get a price out of a
// human. The one thing it must not do is print the heading over nothing and
// let a visitor conclude we have stopped washing cars. The wording covers the
// fetch failing and the fetch returning nothing, because it says only what we
// can be sure of: we cannot show them.
const WashesPending = () => (
  <div className="svc-wait" role="status" aria-live="polite">
    <span className="gwmc-sr-only">Loading our washes and prices.</span>
    <span className="svc-wait-bar" aria-hidden="true" />
    <span className="svc-wait-bar" aria-hidden="true" />
    <span className="svc-wait-bar" aria-hidden="true" />
  </div>
)

const WashesUnavailable = () => (
  <p className="svc-wait-fail">
    We cannot show our wash prices just now, which is us and not you. Reload the page, or{' '}
    <a className="gwmc-link" {...whatsappChatProps('Hi GoWashMyCar! What do your washes cost?')}>
      ask us on WhatsApp
    </a>{' '}
    and we will tell you what your car costs.
  </p>
)

export const Compare = () => {
  // THE LIST, AND WHERE IT COMES FROM.
  //
  // First choice is the gwmc:catalogue meta tag: it is in the document, so the
  // table paints with the page and there is no skeleton and no jump. Second
  // choice is /api/v2/services, which publishes the same CustomerCatalogue
  // table with a `page_rank` on every row, and covers the one case the meta
  // cannot: an HTML document served before that tag existed.
  //
  // Either way the washes, their order, their names and their prices are the
  // database's answer. This file decides none of it. It used to decide all of
  // it, in an array of six, and that is why the page spent a day advertising
  // services that had been retired.
  //
  // Extras owns the fetch on this page (see usePublishedCatalogue), so this
  // reads the store and does not ask for the request itself.
  const published = readWashes()
  const { entries, status } = usePublishedCatalogue({ fetchIfMissing: false })

  const washes = useMemo(() => {
    const fromMeta = washesFrom(published, { descriptors: DESCRIPTORS })

    return fromMeta.length > 0 ? fromMeta : washesFrom(entries, { descriptors: DESCRIPTORS })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [entries])

  return (
    // id="compare": /services#compare is the link every place a customer is
    // choosing a wash points at (Robin, 25 Oct 2026: "the compare washes
    // should be better linked throughout"); ServicesPage scrolls to it.
    <Section id="compare" className="svc-compare">
      {/* The lede and the three frames share a row at 1440: the words are
          --container-read wide and the photographs take the paper that was
          empty to the right of them, so they cost the page no height. */}
      <div className="svc-lede">
        <div className="svc-lede-words">
          {/* One line, and it is spent naming two of the fourteen rows rather
              than describing the table. "Tyres dressed" and "the spare wheel
              area" are the kind of thing somebody only writes down if they
              actually do it, which is the whole argument the page is making.
              Both are rows in ./washes.js: nothing here is claimed that the
              table does not score.

              Both counts are derived. "fourteen jobs" is the matrix's own row
              count and "all six" is how many washes the database published,
              so neither sentence can outlive what it describes. */}
          <SectionHeading
            intro={`The same ${numberWord(FEATURE_COUNT)} jobs on all ${numberWord(
              washes.length
            )}, from tyres dressed to the spare wheel area.`}>
            Compare all {numberWord(washes.length)} washes
          </SectionHeading>

          {/* Two sentences, one per pair, and each one is now one sentence
              rather than three. The descriptors in the table do the rest:
              they answer outside and inside in that order for all six, so
              the only thing left for these to carry is the clause the table
              has no room for. */}
          <div className="svc-pairs">
            {PAIRS.map(pair => (
              <PairNote key={pair.washes.join('-')} pair={pair} washes={washes} />
            ))}
          </div>
        </div>

        <Proof />
      </div>

      {washes.length === 0 ? (
        status === 'loading' ? (
          <WashesPending />
        ) : (
          <WashesUnavailable />
        )
      ) : (
        <>
          <ComparisonTable washes={washes} />

          <div className="svc-cards">
            {washes.map(wash => (
              <WashCard key={wash.slug} wash={wash} />
            ))}
          </div>
        </>
      )}
    </Section>
  )
}
